import postal from 'postal';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import CustomProvider from 'components/CustomProvider';
import { MapExpandProvider } from 'components/MapExpandControl/MapExpandProvider';
import { AdditionalStyleId } from '@alltrails/maps/types/Styles';
import { Trail } from '@alltrails/shared/types/trail';
import type { Context } from 'types/Context';
import { useEffect, useMemo, useState } from 'react';
import useExperiments from 'hooks/useExperiments';
import useListItems from 'hooks/useListItems';
import { ApplyEditsResponse } from 'api/Edits';
import hasPermission from 'utils/hasPermission';
import Waypoint from 'types/Waypoint';
import getTrailProps from 'api/Trail';
import logError from 'utils/logError';
import { getSlidRoute } from 'api/TrailRedraw';
import { LineString } from '@turf/helpers';
import logReplaceRouteButtonClicked from '@alltrails/analytics/events/logReplaceRouteButtonClicked';
import { Page } from '../../../../components/shared/Page/Page';
import { getFormattedTrailUrls } from '../../../../utils/trail_helpers';
import { LanguageSupportUtil } from '../../../../utils/language_support_util';
import ContributeMap from './contribute_map';
import EditTrailEndBox from './edit_trail_end_box';
import EditTrailBox from './EditTrailBox';

type Props = {
  trail?: (ApplyEditsResponse | Trail) & { pctLowTrafficCells10?: number };
  context: Context;
  initialMyLists?: any;
  initialMyListItems?: any;
  allowNameEditing: boolean;
  currentCity: string;
  rapidRoute: boolean;
  enabledOverlays: AdditionalStyleId[];
  mapboxAccessToken: string;
};

const ContributeTrailApp = ({
  trail,
  context,
  initialMyLists,
  initialMyListItems,
  allowNameEditing,
  currentCity,
  rapidRoute,
  enabledOverlays,
  mapboxAccessToken
}: Props) => {
  const formatTrailUrl = (trail: Trail) => getFormattedTrailUrls(trail, context.languageRegionCode).localizedUrl;
  const resultCardFunctions = {
    handleTrailClick: (trail: Trail) => {
      // Open Trail SEO Pages in new window instead of default behavior (opening up Explore trail page)
      window.open(formatTrailUrl(trail), '_blank');
    },
    formatTrailUrl
  };
  const experiments = useExperiments();
  const isWaypointPrivacyEnabled = experiments?.['web-disco-rm-waypoint-privacy']?.value === 'on';
  const [defaultMap, setDefaultMap] = useState<any | null>(trail?.defaultMap);
  const [contributeState, setContributeState] = useState(trail ? 'editTrailActive' : 'editTrailStart');
  const [routeInfo, setRouteInfo] = useState<any | null>(null);
  const [insertRouteInfo, setInsertRouteInfo] = useState<any | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [center, setCenter] = useState<number[] | null>(null);
  const [waypoints, setWaypoints] = useState<Waypoint[] | null>(null);
  const [waypointMapId, setWaypointMapId] = useState<number | null>(null);
  const [areWaypointsLoading, setAreWaypointsLoading] = useState(false);
  const [suggestedRoute, setSuggestedRoute] = useState<LineString | null>(null);
  const [showSuggestedRoute, setShowSuggestedRoute] = useState(false);

  const trailObj = useMemo(
    () => (trail?.id && trail?.name && trail?.slug ? { ID: trail.id, type: 'trail', name: trail.name, slug: trail.slug } : null),
    [trail]
  );

  useEffect(() => {
    const getSuggestedRoute = async () => {
      const trailId = trailObj?.type === 'trail' ? trailObj?.ID : undefined;
      if (!trailId) {
        return;
      }
      try {
        const route = await getSlidRoute(trailId);
        // if no SR exists, the result will be an empty array
        // if an SR does exist, the result will be geometry object
        if (!Array.isArray(route)) {
          setSuggestedRoute(route);
          setShowSuggestedRoute(true);
        }
      } catch (e) {
        // not setting state is sufficient on server error
      }
    };
    if (hasPermission({ permission: 'trails:manage' })) {
      getSuggestedRoute();
    }
  }, [trailObj]);

  const messagingChannel = postal.channel('contribute_messagingChannel');
  const { listMethods, renderModal } = useListItems(
    {
      lists: initialMyLists,
      listItems: initialMyListItems
    },
    PageStrings.CONTRIBUTE_TRAIL
  );

  const refetchTrailProps = async () => {
    if (!trail.id) return;
    if (isWaypointPrivacyEnabled && !hasPermission({ permission: 'trails:manage' })) return;
    try {
      if (trail.id) {
        setAreWaypointsLoading(true);
        const data = await getTrailProps(trail.id, false);
        if (!data.overviewProps) {
          setAreWaypointsLoading(false);
          return;
        }
        if (data.overviewProps) {
          const waypointTab = data.overviewProps.tabs.find(tab => tab.key === 'waypoints');
          setWaypoints(waypointTab.value.waypoints);
          setWaypointMapId(waypointTab.value.id);
          setAreWaypointsLoading(false);
        }
      }
    } catch (e) {
      setAreWaypointsLoading(false);
      logError(`Error fetching trail props: ${e}`);
    }
  };

  useEffect(() => {
    if (trail) {
      refetchTrailProps();
    }
  }, [trail]);

  const handleWaypointsChanged = (data: any) => {
    let { waypoints } = data;
    const { editWaypointId, activeWaypoint } = data;

    // if a waypoint is new and doesn't have a lat / lng, we want to coordinate it with the map
    if (typeof activeWaypoint !== 'undefined' && activeWaypoint.location.longitude === null && activeWaypoint.location.latitude === null) {
      const latitude = center ? center[0] : (trail as ApplyEditsResponse).location.latitude;
      const longitude = center ? center[1] : (trail as ApplyEditsResponse).location.longitude;
      waypoints = waypoints.map((wp: Waypoint) => {
        if (wp.id === null) {
          return { ...wp, location: { longitude, latitude } };
        }

        return wp;
      });
      messagingChannel.publish('waypoint.dragged.with_location', { waypointId: activeWaypoint.id, location: { latitude, longitude } });
    }

    setDefaultMap({ ...defaultMap, waypoints, editWaypointId });
    setWaypoints(waypoints);
  };

  useEffect(() => {
    if (!messagingChannel) {
      return;
    }
    messagingChannel.subscribe('waypointsChanged', handleWaypointsChanged);
  }, [center]);

  const handleRouteInfoChanged = (routeInfoChanges: any) => {
    if (!routeInfoChanges) {
      setRouteInfo(null);
      return;
    }
    // if this is called twice in the same render, the value of routeInfo will not been updated from the first call.
    // use functional update to ensure the latest value is used.
    setRouteInfo((previousRouteInfo: any) => (previousRouteInfo ? { ...previousRouteInfo, ...routeInfoChanges } : routeInfoChanges));
  };

  const handleInsertRouteInfoChanged = (routeInfoChanges: any) => {
    if (!routeInfoChanges) {
      setInsertRouteInfo(null);
      return;
    }
    // if this is called twice in the same render, the value of routeInfo will not been updated from the first call.
    // use functional update to ensure the latest value is used.
    setInsertRouteInfo((previousInsertRouteInfo: any) =>
      previousInsertRouteInfo ? { ...previousInsertRouteInfo, ...routeInfoChanges } : routeInfoChanges
    );
  };

  const handleMapChange = (defaultMap: any) => {
    setDefaultMap(defaultMap);
  };

  // When an admin replaced the route with the suggested route
  const handleReplaceRoute = () => {
    messagingChannel.publish('route.replace', { suggestedRoute });
    logReplaceRouteButtonClicked({ trail_id: trail?.id });
  };

  const setMapData = ({ center, zoom }: { center: number[]; zoom: number }) => {
    setCenter(center);
    setZoom(zoom);
  };

  const endEditTrail = () => {
    setContributeState('editTrailEnd');
  };

  const handleAddTrailClick = () => {
    const url = LanguageSupportUtil.wrapUrlSafe(
      `/trail/new?latitude=${center?.[0]}&longitude=${center?.[1]}&zoom=${zoom}`,
      context.languageRegionCode
    );
    window.open(url, '_blank');
  };

  // TypeScript complains without this
  const EditTrailBoxProps = {
    allowNameEditing,
    context,
    currentCity,
    displayMetric: context ? context.displayMetric : null,
    handleAddTrailClick,
    key: `editTrail-${trail?.id}`,
    mapCenter: center,
    zoom,
    messagingChannel,
    nextTrailCallback: endEditTrail,
    rapidRoute,
    routeInfo,
    insertRouteInfo,
    trail,
    handleMapChange,
    defaultMap,
    mapboxAccessToken,
    waypointMapId,
    waypoints,
    refetchTrailProps,
    areWaypointsLoading,
    hasSuggestedRoute: suggestedRoute !== null,
    showSuggestedRoute,
    onToggleShowSuggestedRoute: () => setShowSuggestedRoute(!showSuggestedRoute),
    handleReplaceRoute
  };

  const contributeBox =
    // eslint-disable-next-line no-nested-ternary
    contributeState === 'editTrailActive' ? (
      <EditTrailBox
        {...EditTrailBoxProps}
        hasAdminFeatures={hasPermission({ permission: 'trails:manage' })}
        hideLegacyWaypoints={isWaypointPrivacyEnabled}
      />
    ) : contributeState === 'editTrailEnd' ? (
      <EditTrailEndBox key="editTrailEnd" trail={trail} context={context} />
    ) : null;

  let initialCenter;
  if (trail && (trail as ApplyEditsResponse)) {
    const { latitude, longitude, zoom } = (trail as ApplyEditsResponse).location;
    if (zoom && latitude && longitude) {
      initialCenter = [latitude, longitude, zoom];
    }
  }

  useEffect(() => {
    if (defaultMap && trail && !defaultMap?.trailId) {
      setDefaultMap({ ...defaultMap, trailId: trail.id });
    }
  }, [trail]);

  return (
    <Page context={context}>
      <div id="contribute-fullscreen" className="full-height" data-testid="contribute-fullscreen">
        <div id="modalPortal" />
        {contributeBox}
        {renderModal?.()}
        <MapExpandProvider>
          <ContributeMap
            context={context}
            currentPage={PageStrings.CONTRIBUTE_TRAIL}
            enabledOverlays={enabledOverlays}
            exploreMap={defaultMap}
            profilePhoto={trail?.defaultPhoto}
            hasAdminFeatures={hasPermission({ permission: 'trails:manage' })}
            hideLegacyWaypoints={isWaypointPrivacyEnabled && !hasPermission({ permission: 'trails:manage' })}
            id="contribute-map"
            initialCenter={initialCenter}
            mapboxAccessToken={mapboxAccessToken}
            mapDivId="map"
            mapProviderName="mapBox"
            messagingChannel={messagingChannel}
            plannerMap={routeInfo ? routeInfo.plannerMap : null}
            handleRouteInfoChanged={handleRouteInfoChanged}
            handleInsertRouteInfoChanged={handleInsertRouteInfoChanged}
            resultCardFunctions={resultCardFunctions}
            selectedObject={trailObj}
            setMapData={setMapData}
            setMapInitialData={setMapData}
            initialMyListItems={initialMyListItems}
            initialMyLists={initialMyLists}
            listMethods={listMethods}
            experiments={experiments}
            suggestedRoute={suggestedRoute}
            showSuggestedRoute={showSuggestedRoute}
          />
        </MapExpandProvider>
      </div>
    </Page>
  );
};

export default function ContributeTrailAppWrapper(props: Props) {
  return (
    <CustomProvider>
      <ContributeTrailApp {...props} />
    </CustomProvider>
  );
}
